import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/organisms/dialog";
import { useApi } from "@/lib/api-client";
import { authClient, useSession } from "@/lib/auth-client";

type Plan = {
  workspaces: {
    deleted: Array<{ id: string; name: string }>;
    handedOver: Array<{ id: string; name: string; to: string }>;
  };
};

/**
 * Settings → Delete account (AUTH.md → "Deleting an account"): the dialog lists what happens to
 * the workspaces you own, then asks for a 6-digit code emailed to you, so a session alone can't
 * delete an account.
 */
export function DangerZoneSection() {
  const { data: session } = useSession();
  const userEmail = session?.user?.email ?? "";
  const client = useApi();
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<"code" | "delete" | null>(null);
  const [error, setError] = useState("");

  function resetState() {
    setPlan(null);
    setCodeSent(false);
    setCode("");
    setBusy(null);
    setError("");
  }

  async function handleOpen(next: boolean) {
    setOpen(next);
    if (!next) return resetState();
    try {
      const res = await client.api.users.me.deletion.$get();
      if (res.ok) setPlan((await res.json()) as Plan);
    } catch {
      // The list is a courtesy; deleting still works without it.
    }
  }

  async function sendCode() {
    setBusy("code");
    setError("");
    try {
      const res = await client.api.users.me.delete.code.$post();
      const body = (await res.json()) as { error?: string };
      if (!res.ok) setError(body.error ?? "Couldn't send a code. Try again.");
      else setCodeSent(true);
    } catch {
      setError("Network error. Please try again.");
    }
    setBusy(null);
  }

  async function handleDelete() {
    if (!/^\d{6}$/.test(code)) return;
    setBusy("delete");
    setError("");
    try {
      const res = await client.api.users.me.delete.$post({ json: { code } });
      if (!res.ok) {
        const body = (await res.json()) as { error?: string };
        setError(body?.error ?? "Failed to delete account");
        setCode("");
        setBusy(null);
        return;
      }
      await authClient.signOut();
    } catch {
      setError("Network error. Please try again.");
      setBusy(null);
    }
  }

  const deleted = plan?.workspaces.deleted ?? [];
  const handedOver = plan?.workspaces.handedOver ?? [];

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-destructive">Danger zone</h2>

      <div className="rounded-lg border border-destructive/30 p-4 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Delete your account</p>
            <p className="text-sm text-muted-foreground mt-1">
              This will permanently delete your account, all chat history, and all associated data.
              This action cannot be undone.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => void handleOpen(true)}
          className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-white hover:bg-destructive/90 transition-colors"
        >
          Delete account
        </button>
      </div>

      <Dialog open={open} onOpenChange={(next) => void handleOpen(next)}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="sr-only">Delete account confirmation</DialogTitle>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="size-5 text-destructive" />
              </div>
              <div>
                <p className="text-sm font-semibold">Delete your account?</p>
                <p className="text-sm text-muted-foreground">
                  This action is permanent and cannot be undone.
                </p>
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            {(deleted.length > 0 || handedOver.length > 0) && (
              <div className="space-y-2 text-sm">
                {deleted.length > 0 && (
                  <p className="text-muted-foreground">
                    Deleted with it (nobody else is in them):{" "}
                    <span className="text-foreground">{deleted.map((w) => w.name).join(", ")}</span>
                  </p>
                )}
                {handedOver.map((w) => (
                  <p key={w.id} className="text-muted-foreground">
                    <span className="text-foreground">{w.name}</span> goes to {w.to}.
                  </p>
                ))}
              </div>
            )}

            {!codeSent ? (
              <p className="text-sm text-muted-foreground">
                We'll email a 6-digit code to <span className="text-foreground">{userEmail}</span>{" "}
                to confirm it's you.
              </p>
            ) : (
              <div className="space-y-2">
                <label htmlFor="delete-confirm-code" className="text-sm font-medium">
                  Enter the code we sent to {userEmail}
                </label>
                <input
                  id="delete-confirm-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm tracking-[0.4em] outline-none focus:ring-2 focus:ring-destructive/30 focus:border-destructive"
                  placeholder="000000"
                />
                <button
                  type="button"
                  onClick={sendCode}
                  disabled={busy !== null}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Send a new code
                </button>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              {!codeSent ? (
                <button
                  type="button"
                  onClick={sendCode}
                  disabled={busy !== null}
                  className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-white hover:bg-destructive/90 transition-colors disabled:opacity-50"
                >
                  {busy === "code" ? "Sending..." : "Email me a code"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={busy !== null || code.length !== 6}
                  className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-white hover:bg-destructive/90 transition-colors disabled:opacity-50"
                >
                  {busy === "delete" ? "Deleting..." : "Yes, delete my account"}
                </button>
              )}
              <button
                type="button"
                onClick={() => handleOpen(false)}
                disabled={busy === "delete"}
                className="rounded-lg border border-border bg-background px-4 py-2 text-sm hover:bg-muted transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
